import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { handle } from '@/lib/displayName';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import LiveTrackingMap from './LiveTrackingMapLeaflet';
import LocationTracker from './LocationTracker';
import usePushNotifications from '../hooks/usePushNotifications';
import { 
  Car, MapPin, Phone, Star, Navigation, CheckCircle, 
  Loader2, AlertCircle, Clock, User, Map, Bell, BellOff,
  Instagram, Facebook, Link2, Calendar, PoundSterling, ArrowLeftRight, ArrowRight,
  MessageSquare, ExternalLink, XCircle, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status display configuration
const STATUS_CONFIG = {
  inactive: { label: 'Waiting', color: 'bg-gray-500', icon: Clock },
  en_route_to_pickup: { label: 'Driver On The Way', color: 'bg-blue-500', icon: Car },
  passenger_collected: { label: 'In Transit', color: 'bg-green-500', icon: Navigation },
  completed: { label: 'Completed', color: 'bg-success', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle }
};

// Calculate refund based on time before trip
const calculateRefund = (booking) => {
  if (!booking?.total_pence) return { refundPence: 0, refundPercent: 0, reason: 'No refund available' };
  
  // Get the pickup/trip time
  const pickupTime = booking.pickup_point?.time || booking.pickup_time;
  const eventDate = booking.event_date || booking.trip_date || booking.date;
  
  let tripDateTime;
  if (eventDate && pickupTime) {
    // Combine event date with pickup time
    const datePart = new Date(eventDate).toISOString().split('T')[0];
    tripDateTime = new Date(`${datePart}T${pickupTime}:00`);
  } else if (eventDate) {
    tripDateTime = new Date(eventDate);
  } else if (pickupTime) {
    // If we only have pickup time but no date, assume it's today or in the future
    // For safety, give full refund since we can't determine exact time
    return { refundPence: booking.total_pence, refundPercent: 100, reason: 'Full refund (trip date unavailable)' };
  } else {
    // No trip date available - give full refund to be safe
    return { refundPence: booking.total_pence, refundPercent: 100, reason: 'Full refund available' };
  }
  
  const now = new Date();
  const hoursUntilTrip = (tripDateTime - now) / (1000 * 60 * 60);
  
  // Refund policy: Full refund if cancelled more than 1hr before trip, no refund if less than 1hr
  if (hoursUntilTrip >= 1) {
    return { refundPence: booking.total_pence, refundPercent: 100, reason: 'Full refund (more than 1 hour before trip)' };
  } else if (hoursUntilTrip > 0) {
    return { refundPence: 0, refundPercent: 0, reason: 'No refund (less than 1 hour before trip)' };
  } else {
    return { refundPence: 0, refundPercent: 0, reason: 'No refund (trip has already started)' };
  }
};

const DriverDetailsCard = ({ booking, isDriver = false, onStartTrip, onPassengerCollected, onComplete, onCancel, showBookingDetails = true }) => {
  const navigate = useNavigate();
  const [driverDetails, setDriverDetails] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [eta, setEta] = useState(null);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const socketRef = useRef(null);
  const etaIntervalRef = useRef(null);
  
  // Push notifications
  const { isSupported, isSubscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();

  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');

  // Fetch ETA
  const fetchEta = async () => {
    if (!booking?.booking_id) return;
    try {
      const response = await axios.get(`${API}/tracking/${booking.booking_id}/eta`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEta(response.data);
    } catch (error) {
      console.error('Failed to fetch ETA:', error);
    }
  };

  // Fetch tracking status - separate function for polling
  const fetchTrackingStatus = async () => {
    if (!booking?.booking_id) return;
    try {
      const response = await axios.get(`${API}/tracking/${booking.booking_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTracking(prev => ({
          ...prev,
          status: response.data.status,
          current_location: response.data.current_location || prev?.current_location
        }));
        // If tracking is active, also fetch ETA
        if (response.data.status && response.data.status !== 'inactive' && response.data.status !== 'completed') {
          fetchEta();
        }
      }
    } catch (error) {
      console.log('Tracking not active yet');
    }
  };

  useEffect(() => {
    if (!booking?.booking_id) return;

    const fetchDriverDetails = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${booking.booking_id}/driver-details`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDriverDetails(response.data);
        setTracking({
          status: response.data.tracking_status,
          current_location: response.data.current_location
        });
        
        // Fetch initial ETA if tracking is active
        if (response.data.tracking_status && response.data.tracking_status !== 'inactive') {
          fetchEta();
        }
      } catch (error) {
        console.error('Failed to fetch driver details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDriverDetails();

    // Set up Socket.IO connection for real-time updates
    const socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('Connected to tracking socket');
      socket.emit('join_booking_room', { booking_id: booking.booking_id });
    });

    socket.on('location_update', (location) => {
      console.log('Location update received:', location);
      setTracking(prev => ({
        ...prev,
        current_location: location
      }));
      // Refresh ETA when location updates
      fetchEta();
    });

    socket.on('status_update', (data) => {
      setTracking(prev => ({
        ...prev,
        status: data.status
      }));
    });

    socketRef.current = socket;
    
    // Poll tracking status AND ETA every 5 seconds when tracking is active (fallback for Socket.IO)
    // This ensures real-time updates even if WebSocket fails
    etaIntervalRef.current = setInterval(() => {
      fetchTrackingStatus();
    }, 5000);

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_booking_room', { booking_id: booking.booking_id });
        socketRef.current.disconnect();
      }
      if (etaIntervalRef.current) {
        clearInterval(etaIntervalRef.current);
      }
    };
  }, [booking?.booking_id, token]);

  const handleStartTrip = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/tracking/start-trip`, 
        { booking_id: booking.booking_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTracking(prev => ({ ...prev, status: 'en_route_to_pickup' }));
      toast.success('Trip started! Passenger can now track your location.');
      onStartTrip?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePassengerCollected = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/tracking/passenger-collected`, 
        { booking_id: booking.booking_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTracking(prev => ({ ...prev, status: 'passenger_collected' }));
      toast.success('Passenger collected! Safe travels.');
      onPassengerCollected?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteTrip = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/tracking/complete-trip`, 
        { booking_id: booking.booking_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTracking(prev => ({ ...prev, status: 'completed' }));
      toast.success('Trip completed! Thank you for driving.');
      onComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete trip');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!driverDetails) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-6 text-center text-muted-foreground">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p>Driver details not available</p>
        </CardContent>
      </Card>
    );
  }

  const statusConfig = STATUS_CONFIG[tracking?.status] || STATUS_CONFIG.inactive;
  const StatusIcon = statusConfig.icon;

  return (
    <Card className="bg-card/50 border-border/50 overflow-hidden">
      {/* Status Banner */}
      <div className={`${statusConfig.color} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2 text-white">
          <StatusIcon className="w-5 h-5" />
          <span className="font-medium">{statusConfig.label}</span>
        </div>
        {tracking?.status === 'en_route_to_pickup' && tracking?.current_location && (
          <span className="text-white/80 text-sm">Live tracking active</span>
        )}
      </div>

      <CardContent className="p-6 relative overflow-hidden">
        {/* Cancelled Stamp */}
        {booking?.status === 'cancelled' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="text-red-500/25 font-black text-6xl uppercase tracking-widest -rotate-12 border-[6px] border-red-500/25 px-8 py-3 rounded-lg select-none">
              Cancelled
            </div>
          </div>
        )}
        {/* Booking Details - Event, Trip Type, Cost */}
        {showBookingDetails && booking && (
          <div className="mb-6 space-y-3">
            {/* Event Name */}
            {booking.event_name && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="font-semibold text-lg">{booking.event_name}</span>
              </div>
            )}
            
            {/* Trip Details Row */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {/* Trip Type */}
              <div className="flex items-center gap-1.5">
                {booking.is_return_trip ? (
                  <ArrowLeftRight className="w-4 h-4 text-secondary" />
                ) : (
                  <ArrowRight className="w-4 h-4 text-secondary" />
                )}
                <span>{booking.is_return_trip ? 'Return trip' : 'One-way'}</span>
              </div>
              
              {/* Distance */}
              {booking.distance_miles && (
                <div className="flex items-center gap-1.5">
                  <Navigation className="w-4 h-4 text-accent" />
                  <span>{booking.distance_miles?.toFixed(1)} miles</span>
                </div>
              )}
              
              {/* Pickup Time */}
              {booking.pickup_point?.time && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>Pickup at {booking.pickup_point.time}</span>
                </div>
              )}
            </div>
            
            {/* Cost & Payment Status */}
            {booking.total_pence && (
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-primary" />
                  <span className="font-bold text-xl text-primary">
                    £{(booking.total_pence / 100).toFixed(2)}
                  </span>
                </div>
                {booking.payment_status === 'paid' ? (
                  <Badge className="bg-success/20 text-success border-success/30">
                    <CheckCircle className="w-3 h-3 mr-1" /> Paid
                  </Badge>
                ) : (
                  <Badge className="bg-accent/20 text-accent border-accent/30">
                    Payment pending
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        {/* Push Notification Toggle - For passengers only */}
        {!isDriver && isSupported && (
          <div className="mb-4 p-3 bg-muted/30 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isSubscribed ? (
                <Bell className="w-5 h-5 text-primary" />
              ) : (
                <BellOff className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <p className="text-sm font-medium">
                  {isSubscribed ? 'Notifications enabled' : 'Get notified'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSubscribed 
                    ? "You'll be alerted when your driver starts" 
                    : "Enable to know when driver is on the way"}
                </p>
              </div>
            </div>
            <Button
              variant={isSubscribed ? "outline" : "default"}
              size="sm"
              onClick={isSubscribed ? unsubscribe : subscribe}
              disabled={pushLoading}
              className="rounded-full"
            >
              {pushLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isSubscribed ? (
                'Disable'
              ) : (
                'Enable'
              )}
            </Button>
          </div>
        )}

        {/* Driver Info - Uber-style (show for passengers) */}
        {!isDriver && (
          <div className="flex items-start gap-4 mb-6">
            <Avatar 
              className="w-20 h-20 border-4 border-white/50 cursor-pointer hover:border-primary transition-colors"
              onClick={() => navigate(`/profile/${booking.driver_id}`)}
            >
              <AvatarImage src={driverDetails.driver_photo} alt={driverDetails.driver_name} />
              <AvatarFallback className="bg-primary/20 text-white text-2xl">
                {driverDetails.driver_name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Your Driver</p>
              <h3 
                className="font-semibold text-xl cursor-pointer hover:text-primary transition-colors inline-flex items-center gap-2"
                onClick={() => navigate(`/profile/${booking.driver_id}`)}
              >
                {driverDetails.driver_name}
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </h3>
              {driverDetails.driver_rating > 0 ? (
                <div className="flex items-center gap-2 mt-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium">{driverDetails.driver_rating?.toFixed(1)}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">New driver</span>
                </div>
              )}
              
              {/* Community Verified Badge */}
              {driverDetails.community_verified && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-primary/20 text-primary border-primary/30 flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    Community Verified
                  </Badge>
                  {driverDetails.instagram_handle && (
                    <a 
                      href={`https://instagram.com/${driverDetails.instagram_handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors"
                      title={`@${driverDetails.instagram_handle}`}
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {driverDetails.facebook_handle && (
                    <a 
                      href={`https://facebook.com/${driverDetails.facebook_handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors"
                      title={`@${driverDetails.facebook_handle}`}
                    >
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}
              
              {driverDetails.driver_phone && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(`tel:${driverDetails.driver_phone}`, '_self')}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(`sms:${driverDetails.driver_phone}`, '_self')}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Text
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full bg-green-600/10 border-green-600/30 text-green-500 hover:bg-green-600/20"
                    onClick={() => {
                      const phone = driverDetails.driver_phone.replace(/[^0-9]/g, '');
                      window.open(`https://wa.me/${phone}`, '_blank');
                    }}
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                        const r = await axios.post(`${API}/conversations/start?booking_id=${booking?.booking_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        navigate(`/chat/${r.data.conversation_id}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Could not start conversation');
                      }
                    }}
                    data-testid="driver-card-message-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Message
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Passenger Info (show for drivers) */}
        {isDriver && booking && (
          <div className="flex items-start gap-4 mb-6">
            <Avatar 
              className="w-20 h-20 border-4 border-secondary/20 cursor-pointer hover:border-secondary transition-colors"
              onClick={() => navigate(`/profile/${booking.rider_id}`)}
            >
              <AvatarImage src={booking.rider_photo} alt={booking.rider_name} />
              <AvatarFallback className="bg-secondary/20 text-secondary text-2xl">
                {booking.rider_name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Your Passenger</p>
              <h3 
                className="font-semibold text-xl cursor-pointer hover:text-secondary transition-colors inline-flex items-center gap-2"
                onClick={() => navigate(`/profile/${booking.rider_id}`)}
              >
                {booking.rider_name}
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </h3>
              {booking.rider_phone && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(`tel:${booking.rider_phone}`, '_self')}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(`sms:${booking.rider_phone}`, '_self')}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Text
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full bg-green-600/10 border-green-600/30 text-green-500 hover:bg-green-600/20"
                    onClick={() => {
                      const phone = booking.rider_phone.replace(/[^0-9]/g, '');
                      window.open(`https://wa.me/${phone}`, '_blank');
                    }}
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                        const r = await axios.post(`${API}/conversations/start?booking_id=${booking?.booking_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        navigate(`/chat/${r.data.conversation_id}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Could not start conversation');
                      }
                    }}
                    data-testid="passenger-card-message-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Message
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vehicle Info - Prominent like Uber (show for passengers only) */}
        {!isDriver && (driverDetails.vehicle_make || driverDetails.vehicle_registration) && (
          <div className="bg-muted/50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vehicle</p>
                <p className="font-semibold text-lg">
                  {driverDetails.vehicle_color && <span className="capitalize">{driverDetails.vehicle_color} </span>}
                  {driverDetails.vehicle_make} {driverDetails.vehicle_model}
                </p>
              </div>
              {driverDetails.vehicle_registration && (
                <div className="bg-background rounded-lg px-4 py-2 border-2 border-foreground">
                  <p className="font-mono font-bold text-lg tracking-wider">
                    {driverDetails.vehicle_registration}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pickup Point */}
        {driverDetails.pickup_point && (
          <div className="flex items-start gap-3 mb-6">
            <MapPin className="w-5 h-5 text-secondary mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Pickup Location</p>
              <p className="font-medium">{driverDetails.pickup_point.address}</p>
              {driverDetails.pickup_point.notes && (
                <p className="text-sm text-muted-foreground mt-1">{driverDetails.pickup_point.notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Message when driver hasn't started trip yet - for passengers */}
        {!isDriver && (!tracking?.status || tracking?.status === 'inactive') && (
          <div className="bg-muted/50 border border-border/50 rounded-xl p-6 mb-6 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <Map className="w-6 h-6 text-primary" />
            </div>
            <p className="text-muted-foreground">
              Track driver will be available once the driver has started the trip.
            </p>
          </div>
        )}

        {/* Driver Controls - Above the map for easy access */}
        {isDriver && (
          <div className="space-y-3 mb-6">
            {(!tracking?.status || tracking?.status === 'inactive') && (
              <Button 
                onClick={handleStartTrip}
                disabled={actionLoading}
                className="w-full rounded-full bg-primary hover:bg-primary/90"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Car className="w-4 h-4 mr-2" />}
                Start Trip
              </Button>
            )}
            
            {tracking?.status === 'en_route_to_pickup' && (
              <Button 
                onClick={handlePassengerCollected}
                disabled={actionLoading}
                className="w-full rounded-full bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <User className="w-4 h-4 mr-2" />}
                Tap When Passenger Collected
              </Button>
            )}
            
            {tracking?.status === 'passenger_collected' && (
              <Button 
                onClick={handleCompleteTrip}
                disabled={actionLoading}
                className="w-full rounded-full bg-success hover:bg-success/90"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Complete Trip
              </Button>
            )}
          </div>
        )}

        {/* Live Map with ETA */}
        {tracking?.status && tracking?.status !== 'inactive' && tracking?.status !== 'completed' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium flex items-center gap-2">
                <Map className="w-4 h-4 text-blue-500" />
                Live Tracking
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMap(!showMap)}
                className="text-xs"
              >
                {showMap ? 'Hide Map' : 'Show Map'}
              </Button>
            </div>
            
            {/* Location Tracker for Driver - Show right below Live Tracking header */}
            {isDriver && tracking?.status && tracking?.status !== 'inactive' && tracking?.status !== 'completed' && (
              <div className="mb-4">
                <LocationTracker
                  bookingId={booking.booking_id}
                  isActive={true}
                  onLocationUpdate={(location) => {
                    setTracking(prev => ({
                      ...prev,
                      current_location: location
                    }));
                  }}
                />
              </div>
            )}
            
            {/* Debug info - shows driver location status */}
            {!tracking?.current_location && (
              <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-600">
                Waiting for driver's location... Make sure the driver has started sharing their location.
              </div>
            )}
            
            {showMap && (
              <LiveTrackingMap
                driverLocation={tracking?.current_location}
                pickupLocation={driverDetails.pickup_point ? {
                  lat: driverDetails.pickup_point.lat,
                  lng: driverDetails.pickup_point.lng
                } : null}
                destinationLocation={driverDetails.destination_location ? {
                  lat: driverDetails.destination_location.lat,
                  lng: driverDetails.destination_location.lng
                } : null}
                eta={eta}
                trackingStatus={tracking?.status}
                carIcon={driverDetails.car_icon || 'sedan'}
                carColor={driverDetails.vehicle_color || 'pink'}
                locationTimestamp={tracking?.current_location?.timestamp}
              />
            )}
          </div>
        )}

        {/* Simple Live Location indicator (when map is hidden or not available) */}
        {tracking?.current_location && tracking?.status !== 'completed' && !showMap && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-sm font-medium text-blue-500">Live Location</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Last updated: {new Date(tracking.current_location.timestamp).toLocaleTimeString()}
            </p>
            {tracking.current_location.speed && (
              <p className="text-sm text-muted-foreground">
                Speed: {Math.round(tracking.current_location.speed)} km/h
              </p>
            )}
            {eta?.eta_available && (
              <p className="text-sm font-medium text-blue-500 mt-2">
                ETA: {eta.duration_text} ({eta.distance_text})
              </p>
            )}
          </div>
        )}

        {/* Safety Note */}
        {!isDriver && tracking?.status === 'passenger_collected' && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mt-4">
            <p className="text-sm text-green-600 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Safety tracking active during your ride
            </p>
          </div>
        )}

        {/* Cancel Trip Button - Show for pending/confirmed bookings */}
        {booking && ['pending', 'confirmed'].includes(booking.status) && onCancel && (
          <div className="mt-6 pt-4 border-t border-border/50">
            <Button
              variant="outline"
              className="w-full rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
              onClick={() => setShowCancelWarning(true)}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel Booking
            </Button>
          </div>
        )}
      </CardContent>

      {/* Cancel Warning Modal */}
      {showCancelWarning && booking && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full bg-card border-destructive/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Cancel Booking?</h3>
                  <p className="text-sm text-muted-foreground">{booking.event_name}</p>
                </div>
              </div>

              {/* Refund Information */}
              {(() => {
                const refund = calculateRefund(booking);
                return (
                  <div className="bg-muted/50 rounded-xl p-4 mb-4">
                    <p className="text-sm font-medium mb-2">Refund Policy</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Original amount:</span>
                        <span>£{(booking.total_pence / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Refund ({refund.refundPercent}%):</span>
                        <span className={refund.refundPercent > 0 ? 'text-green-500 font-bold' : 'text-destructive'}>
                          £{(refund.refundPence / 100).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                        {refund.reason}
                      </p>
                    </div>
                  </div>
                );
              })()}

              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to cancel this booking? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setShowCancelWarning(false)}
                >
                  Keep Booking
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 rounded-full"
                  disabled={cancelLoading}
                  onClick={async () => {
                    setCancelLoading(true);
                    try {
                      await onCancel(booking.booking_id);
                      setShowCancelWarning(false);
                    } catch (error) {
                      toast.error('Failed to cancel booking');
                    } finally {
                      setCancelLoading(false);
                    }
                  }}
                >
                  {cancelLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Cancel Booking
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
};

export default DriverDetailsCard;
